import * as React from 'react';

import { StudentCompletion, escapeHtml } from './shared';

import GradeColor, { GREEN, ORANGE, RED, } from '../../modules/grade_color';
import { Tooltip } from '../../../../Shared/index';

function initialsFor(name: string | null): string {
  const words = (name || '').trim().split(/\s+/).filter(Boolean)
  if (!words.length) { return '' }
  if (words.length === 1) { return words[0][0].toUpperCase() }
  if (words.length === 2) { return `${words[0][0]}${words[1][0]}`.toUpperCase() }
  return `${words[0][0]}${words[words.length - 2][0]}${words[words.length - 1][0]}`.toUpperCase()
}

function nameTagFor(name: string | null): string {
  const [firstName, ...lastNames] = (name || '').replace(/\([^)]*\)/g, ' ').trim().split(/\s+/).filter(Boolean)
  if (!firstName) { return 'Student' }
  const initials = lastNames.map(lastName => lastName.match(/\p{L}/u)?.[0]).filter(Boolean).map(letter => `${letter.toUpperCase()}.`)
  return [firstName, ...initials].join(' ')
}

function scoreBucket(score: number | null | undefined): 'rarely' | 'sometimes' | 'frequently' | 'unscored' {
  const unscored = score === null || score === undefined
  const color = GradeColor(unscored ? null : score / 100)
  if (color === RED) { return 'rarely' }
  if (color === ORANGE) { return 'sometimes' }
  if (color === GREEN) { return 'frequently' }
  return 'unscored'
}

function badgeVisual(student: StudentCompletion): { statusClass: string, statusCopy: string, progressPct?: number } {
  if (student.completed) {
    const score = student.avg_score
    const bucket = scoreBucket(score)
    if (bucket === 'unscored') { return { statusClass: 'completed', statusCopy: 'completed' } }
    return { statusClass: bucket, statusCopy: `${bucket} demonstrated skill (${score}% avg. score)` }
  }
  if (student.started) {
    const pct = student.progress_pct ?? 0
    const bucket = scoreBucket(student.avg_score)
    const scoreCopy = bucket === 'unscored' ? '' : `, ${student.avg_score}% avg. score so far`
    return {
      statusClass: bucket === 'unscored' ? 'in-progress' : `in-progress ${bucket}`,
      statusCopy: `in progress (${pct}% of activities completed${scoreCopy})`,
      progressPct: pct,
    }
  }
  return { statusClass: '', statusCopy: 'assigned, not started' }
}

export function completedCountFor(students: StudentCompletion[]): number {
  return students.filter(student => student.completed).length
}

export const StudentCompletionBadges = ({ students, reportHrefFor, showsNames }: { students: StudentCompletion[], reportHrefFor?: (student: StudentCompletion) => string | undefined, showsNames?: boolean }) => (
  <div className={`student-badges${showsNames ? ' shows-names' : ''}`}>
    {students.map((student, index) => {
      const { statusClass, statusCopy, progressPct } = badgeVisual(student)
      const initials = initialsFor(student.name)
      const label = `${student.name || 'Student'}: ${statusCopy}`
      const className = `student-badge ${statusClass} ${!showsNames && initials.length >= 3 ? 'compact-initials' : ''}`
      const reportHref = reportHrefFor?.(student)
      const contents = (
        <React.Fragment>
          {showsNames ? nameTagFor(student.name) : initials}
          {progressPct !== undefined && (
            <span className="student-badge-progress">
              <span className="student-badge-progress-fill" style={{ width: `${progressPct}%` }} />
            </span>
          )}
        </React.Fragment>
      )
      // a link is named by what it shows first, so voice control finds it
      const badge = reportHref ? (
        <a className={`${className} focus-on-light`} href={reportHref} rel="noopener noreferrer" target="_blank">
          {contents}
          <span className="sr-only"> ({student.name || 'Student'}, {statusCopy}, opens their report in a new window)</span>
        </a>
      ) : (
        <span aria-label={label} className={className} role="img">{contents}</span>
      )
      return (
        <Tooltip
          isTabbable={false}
          // id alone can collide when one student appears via two classes, so the index disambiguates
          key={`${student.id}-${index}`}
          tooltipText={`${escapeHtml(student.name || 'Student')}: ${statusCopy}`}
          tooltipTriggerText={badge}
        />
      )
    })}
  </div>
)
